import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { api, photoUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useSite } from "../context/SiteContext";
import { useBooking } from "../context/BookingContext";
import { Reveal, MaskLines } from "../components/Reveal";
import { ExcursionCard } from "../components/ExcursionCard";
import AltaiMap from "../components/AltaiMap";
import Seo from "../components/Seo";
import { localBusinessLd, personLd } from "../lib/seo";
import { catLabel, durLabel } from "../lib/labels";

const ease = [0.22, 1, 0.36, 1];

/* --------------------------- ROUND PIN CTA --------------------------- */
const PinCTA = ({ to, children }) => (
  <Link to={to} className="group inline-flex items-center gap-3.5" data-testid="hero-cta-pin">
    <span className="w-[52px] h-[52px] rounded-full border border-argali-ivory/35 flex items-center justify-center shrink-0 transition-all duration-300 group-hover:border-argali-gold group-hover:bg-argali-gold group-hover:text-argali-obsidian group-hover:scale-105">
      <ArrowRight size={16} />
    </span>
    <span className="text-[0.76rem] font-bold uppercase tracking-[0.12em] text-argali-ivory">{children}</span>
  </Link>
);

/* ------------------------- HERO DESTINATIONS ------------------------- */
/* Floating destination-card carousel over the hero photo — hover or click
   a card (or the arrows) to make it the active destination; the headline,
   description, background photo and CTA all follow the active card. */
const HeroDestinations = ({ list, active, onActivate }) => {
  const { lang } = useLang();
  if (!list.length) return null;
  const go = (delta) => onActivate((active + delta + list.length) % list.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.7, ease }}
      className="hidden md:block"
    >
      <div className="flex gap-4">
        {list.map((e, i) => (
          <Link
            key={e.id}
            to={`/excursions/${e.slug}`}
            data-testid={`hero-destination-${e.slug}`}
            onMouseEnter={() => onActivate(i)}
            className="group relative w-[150px] h-[220px] shrink-0 overflow-hidden rounded-2xl transition-transform duration-500 ease-out hover:-translate-y-2.5"
            style={{
              boxShadow: active === i ? "0 30px 55px -14px rgba(0,0,0,0.7)" : "0 18px 40px -14px rgba(0,0,0,0.55)",
              outline: active === i ? "1.5px solid #C88A2B" : "none",
              outlineOffset: "-1.5px",
            }}
          >
            <img
              src={photoUrl(e.hero_photo_id)}
              alt={lang === "ru" ? e.title_ru : e.title_en}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,15,0) 45%, rgba(17,17,15,0.86) 100%)" }} />
            <div className="absolute left-3.5 right-3.5 bottom-3.5">
              <span className="block text-[0.6rem] font-bold uppercase tracking-[0.1em] text-argali-sandstone/90">{catLabel(e.category, lang)}</span>
              <span className="block mt-1 text-[0.86rem] font-bold leading-tight text-argali-ivory">{lang === "ru" ? e.title_ru : e.title_en}</span>
            </div>
          </Link>
        ))}
      </div>
      <div className="flex items-center justify-between mt-5">
        <div className="flex gap-2.5">
          <button type="button" aria-label={lang === "ru" ? "Предыдущая" : "Previous"} onClick={() => go(-1)}
            className="w-9 h-9 rounded-full border border-argali-ivory/30 flex items-center justify-center text-argali-ivory transition-colors hover:border-argali-gold hover:bg-argali-gold/10">
            <ArrowRight size={14} className="rotate-180" />
          </button>
          <button type="button" aria-label={lang === "ru" ? "Следующая" : "Next"} onClick={() => go(1)}
            className="w-9 h-9 rounded-full border border-argali-ivory/30 flex items-center justify-center text-argali-ivory transition-colors hover:border-argali-gold hover:bg-argali-gold/10">
            <ArrowRight size={14} />
          </button>
        </div>
        <span className="font-serif text-2xl text-argali-ivory/70">{String(active + 1).padStart(2, "0")}</span>
      </div>
    </motion.div>
  );
};

/* ------------------------------- HERO ------------------------------- */
const Hero = ({ excursions }) => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const destinations = excursions.filter((e) => e.featured).slice(0, 4);
  const [active, setActive] = useState(null);
  const current = active !== null ? destinations[active] : null;
  const currentDesc = current && (lang === "ru"
    ? (current.short_description_ru || current.description_ru)
    : (current.short_description_en || current.description_en));

  return (
    <section ref={ref} className="relative grain overflow-hidden" style={{ height: "100svh" }}>
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src="/photos/photo_confluence.jpg" alt="Слияние Чуи и Катуни"
          className="absolute inset-0 w-full h-full object-cover"
          initial={false}
          animate={{ opacity: current ? 0 : 1 }}
          transition={{ duration: 0.5, ease }}
        />
        {destinations.map((e, i) => (
          <motion.img
            key={e.id}
            src={photoUrl(e.hero_photo_id)}
            alt={lang === "ru" ? e.title_ru : e.title_en}
            className="absolute inset-0 w-full h-full object-cover"
            initial={false}
            animate={{ opacity: active === i ? 1 : 0 }}
            transition={{ duration: 0.5, ease }}
          />
        ))}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,15,0.55) 0%, rgba(17,17,15,0.15) 35%, rgba(17,17,15,0.8) 100%)" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-20">
        <div className="grid md:grid-cols-[1.1fr_auto] gap-10 items-end">
          <div className="min-w-0">
            <motion.img
              src="/brand/argali_emblem_ru_v2.png" alt="Логотип АРГАЛИ" width="543" height="501"
              initial={{ opacity: 0, y: 16, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1, delay: 0.1 }}
              className="h-20 md:h-24 w-auto mb-6 drop-shadow-[0_6px_24px_rgba(0,0,0,0.5)]"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
              className="eyebrow mb-6"
            >
              {t("positioning")}
            </motion.p>

            {current ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.28, ease }}
                >
                  <h1 className="display display-md text-argali-ivory uppercase">
                    {lang === "ru" ? current.title_ru : current.title_en}
                  </h1>
                  {currentDesc && (
                    <p className="mt-8 max-w-md text-argali-ivory/80 text-base md:text-lg leading-relaxed">
                      {currentDesc}
                    </p>
                  )}
                  <div className="mt-10">
                    <PinCTA to={`/excursions/${current.slug}`}>
                      {lang === "ru" ? "Смотреть маршрут" : "View the route"}
                    </PinCTA>
                  </div>
                </motion.div>
              </AnimatePresence>
            ) : (
              <>
                <h1 className="display display-md text-argali-ivory">
                  <MaskLines start delay={0.35} lines={lang === "ru" ? ["ПОЗНАЙ", "ДИКИЙ АЛТАЙ"] : ["DISCOVER", "WILD ALTAI"]} />
                </h1>
                <motion.p
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.9 }}
                  className="mt-8 max-w-md text-argali-ivory/80 text-base md:text-lg leading-relaxed"
                >
                  {lang === "ru" ? "Индивидуальные путешествия по Республике Алтай — без спешки, маленькими группами, с местным гидом." : "Private journeys across the Altai Republic — unhurried, in small groups, with a local guide."}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.05 }}
                  className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                  <Link to="/excursions" className="btn-bronze group" data-testid="hero-cta-explore">
                    {t("cta_explore")} <ArrowRight size={16} className="btn-arrow" />
                  </Link>
                  <Link to="/map" className="btn-outline group" data-testid="hero-cta-map">
                    {t("cta_map")} <ArrowRight size={16} className="btn-arrow" />
                  </Link>
                </motion.div>
              </>
            )}
          </div>

          <HeroDestinations list={destinations} active={active} onActivate={setActive} />
        </div>
      </motion.div>

      <motion.div style={{ opacity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2">
        <span className="text-[0.6rem] uppercase tracking-[0.25em] text-argali-ivory/60">{t("scroll")}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-argali-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
};

/* --------------------------- BRAND STORY --------------------------- */
const BrandStory = () => {
  const { lang } = useLang();
  const lines = lang === "ru"
    ? ["Это дорога.", "Это история.", "Это люди.", "Это горы.", "Это тишина."]
    : ["It is the road.", "It is history.", "It is people.", "It is the mountains.", "It is silence."];
  return (
    <section className="relative py-32 md:py-48 px-5 md:px-10" style={{ background: "#11110F" }}>
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <h2 className="display display-lg text-argali-ivory">
            {lang === "ru" ? "Алтай — " : "Altai is "}
            <span className="text-argali-sandstone/60">{lang === "ru" ? "не просто место." : "not just a place."}</span>
          </h2>
        </Reveal>
        <div className="mt-16 md:mt-24 md:pl-[30%] space-y-3">
          {lines.map((line, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <p className="font-serif text-3xl md:text-5xl text-argali-ivory/85 leading-tight">{line}</p>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <p className="font-serif text-4xl md:text-6xl text-gradient-gold leading-tight pt-4">{lang === "ru" ? "Это АРГАЛИ." : "This is АРГАЛИ."}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------------- ETHNO / WILDLIFE / MTN ---------------------- */
const Pillars = () => {
  const { lang } = useLang();
  const pillars = [
    { key: "01", photo: "photo_karakol_valley", ru: "Этно", en: "Ethno", dRu: "Культура, археология и живые истории Алтая — Каракольская долина, петроглифы, наследие Пазырыкской культуры.", dEn: "Culture, archaeology and living stories of Altai — the Karakol valley, petroglyphs, the Pazyryk culture heritage." },
    { key: "02", photo: "photo_aya", ru: "Легенды", en: "Legends", dRu: "Легенды и мифы Горного Алтая — священные скалы, храм на Патмосе, Чёртов Палец и предания предгорий.", dEn: "Legends and myths of Gorny Altai — sacred rocks, the temple on Patmos, Chertov Palets and foothill folklore." },
    { key: "03", photo: "photo_aktru", ru: "Горы", en: "Mountains", dRu: "Чуйский тракт, Актру, слияние Аргута и Катуни, Марс. Высокогорье и дороги, ведущие всё дальше.", dEn: "Chuysky Trakt, Aktru, the Argut–Katun confluence, Mars. Highlands and roads that lead ever further." },
  ];
  return (
    <section className="relative" style={{ background: "#11110F" }}>
      {pillars.map((p, i) => (
        <div key={p.key} className="relative grain overflow-hidden group border-t border-argali-brown/25" style={{ minHeight: "70vh" }}>
          <img src={photoUrl(p.photo)} alt={lang === "ru" ? p.ru : p.en} className="absolute inset-0 w-full h-full object-cover ken-burns opacity-60" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(17,17,15,0.85) 0%, rgba(17,17,15,0.3) 100%)" }} />
          <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-24 flex items-center" style={{ minHeight: "70vh" }}>
            <div className="max-w-xl">
              <Reveal>
                <span className="font-serif text-6xl text-argali-bronze/70">{p.key}</span>
                <h3 className="display display-md text-argali-ivory mt-3">{lang === "ru" ? p.ru : p.en}</h3>
                <p className="mt-6 text-argali-ivory/75 text-base md:text-lg leading-relaxed">{lang === "ru" ? p.dRu : p.dEn}</p>
              </Reveal>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
};

/* ----------------------------- SEASONS ----------------------------- */
const Seasons = ({ excursions }) => {
  const { t, lang } = useLang();
  const site = useSite();
  const seasons = site?.seasons || {};
  const order = ["spring", "summer", "autumn", "winter"];
  const accents = { spring: "#b06a6a", summer: "#244B36", autumn: "#C88A2B", winter: "#397D94" };
  const count = (s) => excursions.filter((e) => (e.seasons || []).includes(s)).length;

  return (
    <section className="relative py-28 md:py-40 px-5 md:px-10" style={{ background: "#F4F0E7" }}>
      <div className="max-w-[1600px] mx-auto">
        <Reveal>
          <p className="eyebrow" style={{ color: "#A96818" }}>{t("nav_seasons")}</p>
          <h2 className="display display-lg text-argali-obsidian mt-3">
            {lang === "ru" ? "Алтай меняется" : "Altai changes"}<br />
            <span className="text-argali-bronze">{lang === "ru" ? "каждый сезон." : "with every season."}</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
          {order.map((s, i) => {
            const sp = seasons[s] || {};
            return (
              <Reveal key={s} delay={i * 0.08}>
                <Link to={`/seasons/${s}`} data-testid={`season-card-${s}`} className="group relative block overflow-hidden aspect-[16/10]">
                  <img src={photoUrl(sp.hero_photo_id)} alt={sp.name_ru} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,15,0.15), rgba(17,17,15,0.82))" }} />
                  <div className="absolute top-5 left-5 w-10 h-[3px]" style={{ background: accents[s] }} />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="flex items-end justify-between">
                      <h3 className="display display-md text-argali-ivory">{lang === "ru" ? sp.name_ru : sp.name_en}</h3>
                      <span className="text-argali-sandstone text-sm mb-2">{count(s)} {t("routes_count")}</span>
                    </div>
                    <p className="text-argali-ivory/75 text-sm mt-3 max-w-md">{lang === "ru" ? sp.tagline_ru : sp.tagline_en}</p>
                    <span className="inline-flex items-center gap-2 mt-4 text-[0.68rem] uppercase tracking-[0.16em] text-argali-gold">
                      {t("cta_see_routes")} <ArrowRight size={14} className="btn-arrow" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ----------------------- SIGNATURE JOURNEYS ----------------------- */
const Signature = ({ excursions }) => {
  const { lang } = useLang();
  const fallbackIds = ["chuysky_geyser_classic", "chuysky_mars_geology", "aktru_blue_lake", "teletskoye_winter_ice", "argut_katun_confluence", "chemal_legends"];
  const featured = excursions.filter((e) => e.featured);
  const list = (featured.length ? featured : fallbackIds.map((id) => excursions.find((e) => e.id === id)).filter(Boolean)).slice(0, 6);
  const [active, setActive] = useState(0);
  if (!list.length) return null;

  return (
    <section className="relative py-28 md:py-40 px-5 md:px-10 grain" style={{ background: "#11110F" }}>
      <div className="max-w-[1600px] mx-auto">
        <Reveal>
          <p className="eyebrow">{lang === "ru" ? "Избранные маршруты" : "Signature journeys"}</p>
          <h2 className="display display-lg text-argali-ivory mt-3">
            {lang === "ru" ? "Маршруты, которые" : "Journeys that"}<br />
            <span className="text-argali-sandstone/60">{lang === "ru" ? "запоминаются." : "stay with you."}</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16 items-center">
          <div className="relative aspect-[4/3] overflow-hidden order-2 lg:order-1">
            {list.map((e, i) => (
              <motion.img
                key={e.id}
                src={photoUrl(e.hero_photo_id)}
                alt={lang === "ru" ? e.title_ru : e.title_en}
                className="absolute inset-0 w-full h-full object-cover"
                initial={false}
                animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.06 }}
                transition={{ duration: 0.8, ease }}
              />
            ))}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(17,17,15,0.5))" }} />
          </div>

          <div className="order-1 lg:order-2">
            {list.map((e, i) => (
              <Link
                key={e.id}
                to={`/excursions/${e.slug}`}
                data-testid={`signature-${e.slug}`}
                onMouseEnter={() => setActive(i)}
                className="group flex items-baseline gap-5 py-5 border-b border-argali-brown/30 transition-colors"
              >
                <span className="font-serif text-xl text-argali-bronze/70 w-8">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <h3 className={`font-serif text-2xl md:text-4xl leading-tight transition-colors duration-300 ${active === i ? "text-argali-gold" : "text-argali-ivory/70"}`}>
                    {lang === "ru" ? e.title_ru : e.title_en}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-[0.66rem] uppercase tracking-[0.12em] text-argali-sandstone/60">
                    <span>{durLabel(e.duration, lang)}</span><span>·</span><span>{catLabel(e.category, lang)}</span>
                  </div>
                </div>
                <ArrowRight size={18} className={`btn-arrow transition-opacity ${active === i ? "opacity-100 text-argali-gold" : "opacity-0"}`} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------- MAP ------------------------------- */
const MapSection = ({ features }) => {
  const { lang } = useLang();
  return (
    <section className="relative py-28 md:py-40 px-5 md:px-10" style={{ background: "#F4F0E7" }}>
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <Reveal>
            <p className="eyebrow" style={{ color: "#A96818" }}>{lang === "ru" ? "Карта" : "Map"}</p>
            <h2 className="display display-lg text-argali-obsidian mt-3">
              {lang === "ru" ? "Весь Алтай" : "All of Altai"}<br />
              <span className="text-argali-bronze">{lang === "ru" ? "на одной карте." : "on a single map."}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xs text-argali-brown/70 text-sm leading-relaxed">
              {lang === "ru" ? "Выберите место. Мы покажем, куда ведёт дорога." : "Choose a place. We will show you where the road leads."}
            </p>
          </Reveal>
        </div>
        <Reveal>
          <div className="border border-argali-brown/20">
            <AltaiMap features={features} height="60vh" />
          </div>
          <Link to="/map" className="btn-bronze group mt-6" data-testid="map-section-cta">
            {lang === "ru" ? "Открыть карту" : "Open the map"} <ArrowRight size={16} className="btn-arrow" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------------------- GUIDE INTRO ---------------------------- */
const GuideIntro = () => {
  const { t, lang } = useLang();
  const site = useSite();
  const g = site?.settings?.guide || {};
  return (
    <section className="relative py-28 md:py-40 px-5 md:px-10 grain" style={{ background: "#342116" }}>
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative aspect-[4/5] overflow-hidden bg-argali-obsidian">
            {g.portrait_url ? (
              <img src={g.portrait_url} alt={g.name_ru} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center p-8">
                <img src="/brand/argali_emblem_ru_v2.png" alt="Логотип АРГАЛИ" width="543" height="501" className="w-24 h-auto mb-4 opacity-90" />
                <p className="text-argali-sandstone/60 text-sm uppercase tracking-widest">{lang === "ru" ? "Портрет гида" : "Guide portrait"}</p>
                <p className="text-argali-sandstone/30 text-xs mt-2">{lang === "ru" ? "(добавляется в админ-панели)" : "(set in admin panel)"}</p>
              </div>
            )}
            <div className="absolute top-4 left-4 text-[0.6rem] uppercase tracking-[0.16em] bg-argali-bronze text-argali-white px-3 py-1.5">
              {t("attested")}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="eyebrow">{lang === "ru" ? "О гиде" : "The guide"}</p>
          <h2 className="display display-md text-argali-ivory mt-3">
            {lang === "ru" ? "Алтай, который" : "The Altai"}<br />{lang === "ru" ? "я знаю." : "I know."}
          </h2>
          <p className="mt-6 text-argali-ivory/75 leading-relaxed">{lang === "ru" ? g.story_ru : g.story_en}</p>
          <div className="mt-8 space-y-2 text-sm text-argali-sandstone/80">
            <p><span className="text-argali-sandstone/50 uppercase tracking-widest text-xs">{lang === "ru" ? "Гид" : "Guide"}:</span> {lang === "ru" ? g.name_ru : g.name_en}</p>
            <p><span className="text-argali-sandstone/50 uppercase tracking-widest text-xs">{lang === "ru" ? "Реестр" : "Register"}:</span> {g.guide_number}</p>
          </div>
          <Link to="/guide" className="btn-outline group mt-8" data-testid="guide-intro-cta">
            {t("cta_meet")} <ArrowRight size={16} className="btn-arrow" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

/* -------------------------- PRIVATE POINTS -------------------------- */
const Private = () => {
  const { lang } = useLang();
  const pts = [
    { n: "01", ru: "Без спешки", en: "Unhurried" },
    { n: "02", ru: "Маленькими группами", en: "In small groups" },
    { n: "03", ru: "С местным гидом", en: "With a local guide" },
    { n: "04", ru: "По-настоящему", en: "The real Altai" },
  ];
  return (
    <section className="relative py-28 md:py-40 px-5 md:px-10" style={{ background: "#11110F" }}>
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <h2 className="display display-lg text-argali-ivory">
            {lang === "ru" ? "Ваша экскурсия." : "Your excursion."}<br />
            <span className="text-argali-sandstone/60">{lang === "ru" ? "Ваше путешествие." : "Your journey."}</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
          {pts.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.08}>
              <div className="border-t border-argali-bronze/40 pt-5">
                <span className="font-serif text-4xl text-argali-bronze/70">{p.n}</span>
                <p className="font-serif text-2xl text-argali-ivory mt-3">{lang === "ru" ? p.ru : p.en}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------------------- CUSTOM CTA --------------------------- */
const CustomCTA = () => {
  const { t, lang } = useLang();
  return (
    <section className="relative grain overflow-hidden" style={{ minHeight: "80vh" }}>
      <img src="/photos/photo_katu.jpg" alt="Кату-Ярык" className="absolute inset-0 w-full h-full object-cover ken-burns" />
      <div className="absolute inset-0" style={{ background: "rgba(17,17,15,0.6)" }} />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-center items-center text-center" style={{ minHeight: "80vh" }}>
        <Reveal>
          <h2 className="display display-lg text-argali-ivory">
            {lang === "ru" ? "Ваш Алтай" : "Your Altai"}<br />{lang === "ru" ? "может быть другим." : "can be different."}
          </h2>
          <p className="mt-6 text-argali-ivory/80 max-w-md mx-auto">
            {lang === "ru" ? "Не нашли идеальный маршрут? Мы соберём индивидуальное путешествие." : "Didn't find the perfect route? We'll design a custom journey."}
          </p>
          <Link to="/custom-tour" className="btn-bronze group mt-8" data-testid="custom-cta">
            {t("cta_custom")} <ArrowRight size={16} className="btn-arrow" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------- PAGE ------------------------------- */
export default function Home() {
  const { lang } = useLang();
  const site = useSite();
  const [excursions, setExcursions] = useState([]);
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    api.get("/excursions").then((r) => setExcursions(r.data)).catch(() => {});
    api.get("/geojson").then((r) => setFeatures(r.data.features || [])).catch(() => {});
  }, []);

  const guide = site?.settings?.guide;

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        lang={lang}
        title={lang === "ru" ? "Экскурсии по Горному Алтаю" : "Private excursions in the Altai Republic"}
        jsonLd={[localBusinessLd(lang), guide ? personLd(guide, lang) : null]}
      />
      <Hero excursions={excursions} />
      <BrandStory />
      <Pillars />
      <Seasons excursions={excursions} />
      <Signature excursions={excursions} />
      <MapSection features={features} />
      <GuideIntro />
      <Private />
      <CustomCTA />
    </div>
  );
}
